import React from 'react';
import styled from 'styled-components';
import TinyColor from 'tinycolor2';

import { formatCurrency } from 'shared/helpers/utils';
import { Button } from 'components/core';
import { ConsumerOrder } from 'src/components/embedded-account/account.types';

import { OrderImageGrid } from './order-image-grid';
import { formatOrderDate } from '../../utils/format-order-date';
import { getUniqueOrderImages } from '../../utils/get-unique-order-images';

type OrderCardProps = {
  order: ConsumerOrder;
  onReorder: () => void;
  reorderLoading: boolean;
  onViewOrder: () => void;
};

export function OrderCard({ order, onReorder, reorderLoading, onViewOrder }: OrderCardProps): JSX.Element {
  const { createdAt, orders: orderItems, totalCost } = order;
  const itemCount = orderItems.length;
  const formattedDate = createdAt ? formatOrderDate(createdAt) : '';
  const formattedTotal = formatCurrency(totalCost || '0');
  const uniqueImages = getUniqueOrderImages(orderItems);

  const handleReorderClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onReorder();
  };

  return (
    <Card onClick={onViewOrder} data-testid='order-card'>
      <ProductDetails>
        <OrderImageGrid images={uniqueImages} />
        <Details>
          <OrderDate>{formattedDate}</OrderDate>
          <OrderMeta>
            {itemCount} {itemCount === 1 ? 'Item' : 'Items'}
          </OrderMeta>
        </Details>
      </ProductDetails>
      <PriceAndCta>
        <PriceSection>
          <Price>{formattedTotal}</Price>
          <Disclaimer>*Past order total - current prices may differ.</Disclaimer>
        </PriceSection>
        <ReorderButton
          variant='secondary'
          onClick={handleReorderClick}
          disabled={reorderLoading}
          data-testid='order-card-reorder-button'
        >
          {reorderLoading ? 'Reordering...' : 'Reorder'}
        </ReorderButton>
      </PriceAndCta>
    </Card>
  );
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: space-between;
  padding: 16px;
  width: 264px;
  transition: all 0.2s;

  &:hover {
    border-color: #d2d7da;
    box-shadow: 0 4px 10px 0 #0000000d;
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderDate = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[25]};
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OrderMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[40]};
  line-height: 12px;
`;

const PriceAndCta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Price = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[25]};
  line-height: normal;
`;

const Disclaimer = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[40]};
  line-height: 14px;
`;

const ReorderButton = styled(Button)`
  background: ${({ theme }) => theme.customized.colors.buttonsLinks}1a;
  color: ${({ theme }) => TinyColor.mix(theme.customized.colors.buttonsLinks, '#000', 20).toString()};
  border: none;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  height: 36px;
  text-transform: uppercase;
  white-space: nowrap;
  transition: all 0.2s;
  width: 100%;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.customized.colors.buttonsLinks};
    color: ${({ theme }) => theme.colors.white};
  }
`;
